import { Loader2 } from "lucide-react"
import * as React from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { useT } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import { takePendingOAuth } from "@/lib/plugin-oauth"

/** Where Google sends the browser after consent (GOOGLE_OAUTH_REDIRECT_URI). */
export function GoogleOAuthCallbackPage() {
  const navigate = useNavigate()
  const t = useT()
  const handled = React.useRef(false)

  React.useEffect(() => {
    // StrictMode runs effects twice in development; the code is single-use.
    if (handled.current) return
    handled.current = true

    const params = new URLSearchParams(window.location.search)
    const state = params.get("state") ?? ""
    const code = params.get("code")
    const error = params.get("error")
    // The code must not linger in history or be re-sent on reload.
    window.history.replaceState(null, "", window.location.pathname)

    const pending = state ? takePendingOAuth(state) : null
    if (!pending) {
      toast.error(t("oauthCallback.toasts.startAgain"))
      navigate("/agents", { replace: true })
      return
    }
    const back = `/agents/${pending.agentId}?tab=plugins&plugin=${encodeURIComponent(pending.pluginSlug)}`
    if (error || !code) {
      toast.error(
        error === "access_denied"
          ? t("oauthCallback.toasts.cancelled")
          : t("oauthCallback.toasts.incomplete"),
      )
      navigate(back, { replace: true })
      return
    }

    api
      .completePluginOAuth({ code, state, code_verifier: pending.codeVerifier })
      .then((install) => {
        toast.success(
          t("oauthCallback.toasts.connected", {
            email: install.oauth?.account_email ?? t("oauthCallback.yourGoogleAccountFallback"),
          }),
        )
      })
      .catch((err: unknown) => {
        toast.error(err instanceof ApiError ? err.message : t("oauthCallback.toasts.connectFailed"))
      })
      .finally(() => navigate(back, { replace: true }))
  }, [navigate, t])

  return (
    <div className="text-muted-foreground flex h-full items-center justify-center gap-2 text-sm">
      <Loader2 className="size-4 animate-spin" />
      {t("oauthCallback.connecting")}
    </div>
  )
}
